import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { logger } from '@adornis/base/logging.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription, registerQuery } from '@adornis/baseql/metadata/register.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadRawEntityDataByIDOrFail, loadRawMongoData } from '@adornis/bh-util/util.js';
import { from, switchMap, map, of } from 'rxjs';
import { checkWeatherUserMeetsEntryRequirements } from '../api/product-entry-requirement.js';
import { ProductWaitinglistPriorityRule } from '../entities/ProductWaitinglistPriorityRule.js';
import { Product } from '../entities/products.js';

async function getProductWaitinglistPriorityRulesQueryForProduct(
  productID: string,
  ignoreInstitutionRules: boolean = false,
) {
  logger.info({ productID, ignoreInstitutionRules }, 'getProductWaitinglistPriorityRulesQueryForProduct');
  const product = await loadRawEntityDataByIDOrFail(Product, productID, {
    institutionID: 1,
    overrideInstitutionWaitinglistPriorityRules: 1,
  });

  const institutionID = product.institutionID;
  const query = (() => {
    if (product.overrideInstitutionWaitinglistPriorityRules || ignoreInstitutionRules) return { productID } as const;
    return { productID: undefined, institutionID } as const;
  })();

  return query;
}

export async function getWaitinglistPriorityForUserForProduct(
  userID: string,
  productID: string,
  precomputedEntryReqStatus?: { requirementsMet: true } | { requirementsMet: false; reason: any },
) {
  logger.info({ userID, productID }, 'getWaitinglistPriorityForUserForProduct');
  const rulesQuery = await getProductWaitinglistPriorityRulesQueryForProduct(productID);
  const rulesData = await loadRawMongoData(ProductWaitinglistPriorityRule, rulesQuery);
  const priorityRules = await runInContext(serverContext.sessionID, () =>
    getProductWaitinglistPriorityRulesByIDs(rulesData.map(r => r._id))(
      selectionSet(() => ProductWaitinglistPriorityRule, 3),
    ),
  );

  // entry requirements
  const result = precomputedEntryReqStatus ?? (await checkWeatherUserMeetsEntryRequirements(userID, productID));
  if (!result.requirementsMet) return 'entry_requirements_not_met' as const;

  if (priorityRules.length === 0) return 'high' as const; // use high as default when no rules are found

  for (const priorityRule of priorityRules) {
    try {
      const hasHighPriority = await priorityRule.hasHighPriority(userID);
      if (hasHighPriority) return 'high' as const;
    } catch (e) {
      logger.error({ e }, 'ProductWaitinglistPriorityRule hasHighPriority errored');
    }
  }

  return 'low' as const;
}

export const subscribeProductWaitinglistPriorityRulesForProduct = registerSubscription({
  operationName: 'subscribeProductWaitinglistPriorityRulesForProduct',
  params: [
    { name: 'productID', type: () => ID },
    { name: 'ignoreInstitutionRules', type: () => Boolean },
  ],
  type: () => [ProductWaitinglistPriorityRule],
  resolve:
    (productID: string, ignoreInstitutionRules: boolean = false) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) => {
      const queryPromise = getProductWaitinglistPriorityRulesQueryForProduct(productID, ignoreInstitutionRules);
      return from(queryPromise).pipe(
        switchMap(query => {
          return watchQuery<ProductWaitinglistPriorityRule>(ProductWaitinglistPriorityRule._collectionName, query);
        }),
      );
    },
});

export const subscribeProductWaitinglistPriorityRulesForInstitution = registerSubscription({
  operationName: 'subscribeProductWaitinglistPriorityRulesForInstitution',
  params: [{ name: 'institutionID', type: () => ID }],
  type: () => [ProductWaitinglistPriorityRule],
  resolve:
    (institutionID: string) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
      watchQuery<ProductWaitinglistPriorityRule>(ProductWaitinglistPriorityRule._collectionName, {
        institutionID,
        productID: null,
      }),
});

export const getProductWaitinglistPriorityRulesByIDs = registerQuery({
  operationName: 'getProductWaitinglistPriorityRulesByIDs',
  type: () => [ProductWaitinglistPriorityRule],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
      readEntitiesByIDs(ProductWaitinglistPriorityRule, ids, gqlFields),
});

export const subscribeProductWaitinglistPriorityRulesByIDs = registerSubscription({
  operationName: 'subscribeProductWaitinglistPriorityRulesByIDs',
  type: () => [ProductWaitinglistPriorityRule],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(ProductWaitinglistPriorityRule, ids, gqlFields),
});

export const getAllProductWaitinglistPriorityRules = registerQuery({
  operationName: 'getAllProductWaitinglistPriorityRules',
  type: () => [ProductWaitinglistPriorityRule],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
      readAllEntities(ProductWaitinglistPriorityRule, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllProductWaitinglistPriorityRules = registerSubscription({
  operationName: 'subscribeAllProductWaitinglistPriorityRules',
  type: () => [ProductWaitinglistPriorityRule],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
      watchAllEntities(ProductWaitinglistPriorityRule, gqlFields, { skip, limit, excludeIDs }),
});

export const getProductWaitinglistPriorityRuleByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getProductWaitinglistPriorityRulesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeProductWaitinglistPriorityRuleByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
    id
      ? subscribeProductWaitinglistPriorityRulesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null))
      : of(null);
